import {
  Column,
  GridOptions,
  IRowNode,
  ProcessCellForExportParams,
} from 'ag-grid-community'
import { useMemo } from 'react'
import {
  ColumnType,
  columnTypes,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { DEFAULT_COLUMN_DEF } from '../../../page-properties/bulksheet-properties'
import {
  PROGRAM_STATUSES,
  ProgramOverviewDto,
} from '../../../../infrastructure/api/programs'
import { DivisionProps } from '../../../../lib/functions/division'
import userApi, { UserBasic } from '../../../../lib/functions/user'
import { DISPLAY_DATE_FORMAT } from '../../../../utils/date'
import { useColumnDefs, ProgramGridContext } from './columnDefs'
import { dateVoService } from '../../../../domain/value-object/DateVO'
import DateTimeVO from '../../../../domain/value-object/DateTimeVO'

type ProgramMasterData = {
  divisions: DivisionProps[]
}

type ProgramPermission = {
  currentUserUuid?: string
  isAdmin: boolean
}

const pickUserByExactName = (
  candidates: UserBasic[],
  name: string
): UserBasic | undefined => candidates.find(user => user.name === name)

const resolveDivisionUuid = (
  label: string,
  divisions: DivisionProps[],
  currentUuid?: string
): string => {
  if (!label) return currentUuid ?? ''
  return divisions.find(d => d.displayName === label)?.uuid ?? currentUuid ?? ''
}

const restoreSingleUser = async (
  column: Column,
  node: IRowNode<ProgramOverviewDto> | null | undefined,
  name: string
): Promise<void> => {
  const candidates = await userApi.search(name)
  const matched = pickUserByExactName(candidates, name)
  if (matched && node) node.setDataValue(column, matched)
}

const restoreCollaborators = async (
  column: Column,
  node: IRowNode<ProgramOverviewDto> | null | undefined,
  raw: string
): Promise<void> => {
  const names = raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
  const matched = await Promise.all(
    names.map(async name =>
      pickUserByExactName(await userApi.search(name), name)
    )
  )
  if (node) {
    node.setDataValue(
      column,
      matched.filter((user): user is UserBasic => !!user)
    )
  }
}

export const processProgramCellForClipboard = ({
  column,
  value,
  node,
}: ProcessCellForExportParams<
  ProgramOverviewDto,
  ProgramGridContext
>): string => {
  if (!column) return value ?? ''
  const { field, type } = column.getColDef()
  const types = Array.isArray(type) ? type : type ? [type] : []

  if (!value) return ''

  if (
    field === 'programManager' ||
    field === 'updatedBy' ||
    field === 'createdBy'
  ) {
    return value.name ?? ''
  }
  if (field === 'collaborators') {
    return value.map((user: UserBasic) => user.name).join(', ')
  }
  if (column.getColId() === 'division') {
    return node?.data?.division?.displayName ?? ''
  }
  if (types.includes(ColumnType.date)) {
    const dateVO = dateVoService.construct(value)
    return dateVoService.isValid(dateVO)
      ? dateVoService.format(dateVO, DISPLAY_DATE_FORMAT)
      : ''
  }
  if (types.includes(ColumnType.dateTime)) {
    return new DateTimeVO(value).toString()
  }
  return value ?? ''
}

export const processProgramCellFromClipboard = ({
  column,
  value,
  node,
  context,
}: ProcessCellForExportParams<ProgramOverviewDto, ProgramGridContext>) => {
  const { field } = column.getColDef()

  if (column.getColId() === 'division') {
    return resolveDivisionUuid(
      value,
      context.divisions,
      node?.data?.division?.uuid
    )
  }
  if (
    field === 'programManager' ||
    field === 'updatedBy' ||
    field === 'createdBy'
  ) {
    if (!value) return node?.data?.programManager
    void restoreSingleUser(column, node, value)
    return node?.data?.programManager
  }
  if (field === 'collaborators') {
    if (!value) return node?.data?.collaborators
    void restoreCollaborators(column, node, value)
    return node?.data?.collaborators
  }
  return value
}

export const useProgramGridOptions = (
  masterData: ProgramMasterData,
  permission: ProgramPermission
): GridOptions<ProgramOverviewDto> => {
  const columnDefs = useColumnDefs()
  const { currentUserUuid, isAdmin } = permission

  return useMemo(() => {
    const context: ProgramGridContext = {
      programStatuses: PROGRAM_STATUSES,
      divisions: masterData.divisions,
      currentUserUuid,
      isAdmin,
    }
    return {
      groupHeaderHeight: 25,
      headerHeight: 45,
      floatingFiltersHeight: 25,
      rowHeight: 32,
      treeData: false,
      context,
      columnTypes: columnTypes(),
      components: frameworkComponents,
      defaultColGroupDef: { suppressHeaderContextMenu: true },
      defaultColDef: DEFAULT_COLUMN_DEF,
      columnDefs,
      processCellForClipboard: processProgramCellForClipboard,
      processCellFromClipboard: processProgramCellFromClipboard,
    }
  }, [columnDefs, masterData.divisions, currentUserUuid, isAdmin])
}
